import { useEffect, useState } from "react";
import {
  EmployeeComingSoonView,
  EmployeePortalSidebar,
  EmployeePortalTopbar,
  EmployeeProfileView,
} from "../components/InstateamEmployeeComponents";
import EmployeeAttendancePage from "./EmployeeAttendancePage";
import EmployeeChatPage from "./EmployeeChatPage";
import EmployeeDashboardPage from "./EmployeeDashboardPage";
import EmployeeLeavesPage from "./EmployeeLeavesPage";
import EmployeePayrollPage from "./EmployeePayrollPage";
import EmployeeTasksPage from "./EmployeeTasksPage";
import EmployeeDocumentsPage from "./EmployeeDocumentsPage";
import EmployeeComplaintsPage from "./EmployeeComplaintsPage";
import EmployeeNotificationsPage from "./EmployeeNotificationsPage";
import EmployeePerformancePage from "./EmployeePerformancePage";
import EmployeeAnnouncementsView from "./empAnnouncements";
import EmployeeSettingsView from "./employeeSettings";
import {
  getNotifications as getEmployeeNotifications,
  markNotificationCategoryRead,
} from "../api/instateamNotificationsApi";

const EMPLOYEE_NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: "layoutDashboard" },
  { key: "profile", label: "My Profile", icon: "userCircle", notificationCategory: "profile" },
  { key: "tasks", label: "My Tasks", icon: "checkSquare", notificationCategory: "tasks" },
  { key: "attendance", label: "Attendance", icon: "clock", notificationCategory: "attendance" },
  { key: "leaves", label: "Leaves", icon: "plane", notificationCategory: "leave" },
  { key: "salary", label: "Salary & Payslips", icon: "wallet", notificationCategory: "payroll" },
  { key: "documents", label: "My Documents", icon: "fileText", notificationCategory: "documents" },
  { key: "announcements", label: "Announcements", icon: "megaphone", notificationCategory: "announcements" },
  { key: "chat", label: "Chat", icon: "messageSquare", notificationCategory: "chat" },
  { key: "complaints", label: "My Complaints", icon: "alertCircle", notificationCategory: "complaints" },
  { key: "performance", label: "Performance", icon: "barChart" },
  { key: "settings", label: "Settings", icon: "settings" },
  { key: "logout", label: "Logout", icon: "logOut" },
];

const EMPLOYEE_VIEW_STORAGE_KEY = "instateam-employee-active-view";
const EMPLOYEE_PERSISTED_VIEWS = new Set(
  EMPLOYEE_NAV_ITEMS.filter((item) => item.key !== "logout").map((item) => item.key)
);

function buildSidebarNotificationCounts(payload = {}) {
  const counts = { ...(payload.counts || {}) };
  (payload.notifications || []).forEach((notification) => {
    if (!notification?.unread || !notification.category) return;
    const key = `${notification.category}Unread`;
    counts[key] = Math.max(Number(counts[key] || 0), 1);
  });
  return counts;
}

export default function EmployeePanelPage({ employee, token, onLogout, onEmployeeUpdate }) {
  const [activeView, setActiveView] = useState(() => {
    const storedView = window.localStorage.getItem(EMPLOYEE_VIEW_STORAGE_KEY);
    return EMPLOYEE_PERSISTED_VIEWS.has(storedView) ? storedView : "dashboard";
  });
  const [profileTab, setProfileTab] = useState("personal");
  const [portalSearchText, setPortalSearchText] = useState("");
  const [chatSearchText, setChatSearchText] = useState("");
  const [complaintSearchText, setComplaintSearchText] = useState("");
  const [notificationMode, setNotificationMode] = useState("list");
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationCounts, setNotificationCounts] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const currentTab = EMPLOYEE_NAV_ITEMS.find((item) => item.key === activeView);
  const isChatView = activeView === "chat";
  const isComplaintsView = activeView === "complaints";
  const isTasksView = activeView === "tasks";

  useEffect(() => {
    if (EMPLOYEE_PERSISTED_VIEWS.has(activeView)) {
      window.localStorage.setItem(EMPLOYEE_VIEW_STORAGE_KEY, activeView);
    }
  }, [activeView]);

  function openView(nextView) {
    if (nextView === "notifications") {
      setNotificationMode("list");
    }

    setActiveView(nextView);
  }

  async function clearSidebarNotificationDot(category) {
    if (!category || !token) return;
    setNotificationCounts((current) => ({ ...current, [`${category}Unread`]: 0 }));
    try {
      const payload = await markNotificationCategoryRead(token, category);
      const nextCounts = payload.counts || {};
      setNotificationCounts(nextCounts);
      setNotificationCount(nextCounts.unread || 0);
    } catch (error) {
      // Polling will restore the latest state if the read request fails.
    }
  }

  function handleSidebarSelect(nextView) {
    const item = EMPLOYEE_NAV_ITEMS.find((navItem) => navItem.key === nextView);
    clearSidebarNotificationDot(item?.notificationCategory);
    setActiveView(nextView);
  }

  useEffect(() => {
    if (!token) return undefined;
    const fetchNotificationCount = async () => {
      try {
        const payload = await getEmployeeNotifications(token);
        const nextCounts = buildSidebarNotificationCounts(payload);
        setNotificationCounts(nextCounts);
        setNotificationCount(payload.counts?.unread || 0);
      } catch (error) {
        // Keep the portal usable if notification count fails.
      }
    };
    fetchNotificationCount();
    const intervalId = window.setInterval(fetchNotificationCount, 3000);
    return () => window.clearInterval(intervalId);
  }, [token]);

  return (
    <div className={`employee-portal-shell ${isSidebarOpen ? "" : "sidebar-collapsed"}`}>
      {isSidebarOpen ? (
        <EmployeePortalSidebar
          items={EMPLOYEE_NAV_ITEMS}
          activeView={activeView}
          onSelect={handleSidebarSelect}
          onLogout={onLogout}
          notificationCounts={notificationCounts}
        />
      ) : null}

      <div className="employee-portal-main">
        <EmployeePortalTopbar
          employee={employee}
          onOpenProfile={() => setActiveView("profile")}
          onToggleSidebar={() => setIsSidebarOpen((current) => !current)}
          onOpenNotifications={() => {
            setNotificationMode("list");
            setActiveView("notifications");
          }}
          notificationCount={notificationCount}
          searchText={isChatView ? chatSearchText : isComplaintsView ? complaintSearchText : portalSearchText}
          onSearchChange={isChatView ? setChatSearchText : isComplaintsView ? setComplaintSearchText : setPortalSearchText}
          searchPlaceholder={
            isChatView
              ? "Search people, messages, files..."
              : isTasksView
                ? "Search tasks, projects..."
              : isComplaintsView
                ? "Search complaints..."
                : "Search anything..."
          }
        />

        <main className="employee-portal-content">
          {activeView === "dashboard" ? (
            <EmployeeDashboardPage
              employee={employee}
              token={token}
              searchText={portalSearchText}
              onNavigate={openView}
            />
          ) : activeView === "profile" ? (
            <EmployeeProfileView
              employee={employee}
              profileTab={profileTab}
              onProfileTabChange={setProfileTab}
            />
          ) : activeView === "attendance" ? (
            <EmployeeAttendancePage token={token} searchText={portalSearchText} />
          ) : activeView === "tasks" ? (
            <EmployeeTasksPage token={token} searchText={portalSearchText} />
          ) : activeView === "leaves" ? (
            <EmployeeLeavesPage token={token} searchText={portalSearchText} />
          ) : activeView === "salary" ? (
            <EmployeePayrollPage token={token} searchText={portalSearchText} />
          ) : activeView === "documents" ? (
            <EmployeeDocumentsPage token={token} searchText={portalSearchText} />
          ) : activeView === "announcements" ? (
            <EmployeeAnnouncementsView token={token} searchText={portalSearchText} />
          ) : activeView === "chat" ? (
            <EmployeeChatPage
              token={token}
              searchText={chatSearchText}
              onSearchChange={setChatSearchText}
            />
          ) : activeView === "complaints" ? (
            <EmployeeComplaintsPage token={token} searchText={complaintSearchText} />
          ) : activeView === "performance" ? (
            <EmployeePerformancePage token={token} searchText={portalSearchText} />
          ) : activeView === "notifications" ? (
            <EmployeeNotificationsPage
              token={token}
              mode={notificationMode}
              onModeChange={setNotificationMode}
              onUnreadChange={(counts) => {
                setNotificationCounts(counts || {});
                setNotificationCount(counts?.unread || 0);
              }}
            />
          ) : activeView === "settings" ? (
            <EmployeeSettingsView
              employee={employee}
              token={token}
              onEmployeeUpdate={onEmployeeUpdate}
            />
          ) : (
            <EmployeeComingSoonView title={currentTab?.label || "Dashboard"} />
          )}
        </main>
      </div>
    </div>
  );
}
